import {useApolloClient} from '@apollo/client';
import {InvestmentType, useInvestmentAccountQuery} from '@transactions/graphql';
import Money from 'common/Money';
import * as React from 'react';
import {AccountPortfolioChart} from 'src/account/AccountPortfolioChart';
import {PlaidCard} from 'src/account/PlaidCard';
import {AmountDisplay} from 'src/AmountDisplay';
import {useCurrencyLabel} from 'src/hooks/useUnits';
import type {InvestmentAccount} from 'src/account/AccountDetail';

export const formatInvestmentType = (type: InvestmentType): string => {
  switch (type) {
    case InvestmentType.Brokerage:
      return 'Brokerage';
    case InvestmentType['401K']:
      return '401(k)';
    case InvestmentType.Ira:
      return 'IRA';
    case InvestmentType.RothIra:
      return 'Roth IRA';
    case InvestmentType.Hsa:
      return 'HSA';
    case InvestmentType.Education:
      return '529 plan';
    case InvestmentType.Other:
      return 'Other';
    default:
      return 'Unknown';
  }
};

const formatCurrency = (cents: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);

type Props = {
  account: InvestmentAccount;
  refetchAccount: () => void;
};

/**
 * Overview tab for investment accounts: account facts (type, currency,
 * transaction count, Plaid status, current value), the performance summary,
 * and the portfolio history chart. Snapshots are managed on their own tab.
 */
export const InvestmentOverviewView = ({account, refetchAccount}: Props) => {
  const client = useApolloClient();
  // Performance (cost basis, XIRR) is computed server-side from cash flows
  // and snapshots, so it loads here rather than in the shared shell.
  // cache-and-network keeps it fresh when returning after edits on the
  // Transactions or Snapshots tabs.
  const {data, loading, refetch} = useInvestmentAccountQuery({
    variables: {id: account._id},
    fetchPolicy: 'cache-and-network',
  });
  const performance = data?.investmentAccount?.performance ?? null;

  const currencyLabel = useCurrencyLabel();
  // Present (today-clamped) value once the detail query lands, so the facts
  // strip agrees with the Performance card — account.balance deliberately
  // follows the newest entry, future-dated anchors included.
  const currentValue = Money.fromServer({
    amount: data?.investmentAccount?.presentBalance ?? account.balance,
    currency: account.currency,
  });

  return (
    <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-4 sm:p-8">
      {/* Account facts */}
      <div className="border-border bg-card flex flex-wrap items-start justify-between gap-x-6 gap-y-2 rounded-xl border px-4 py-3">
        <div className="min-w-0">
          <p className="text-muted-foreground m-0 text-sm">
            {formatInvestmentType(account.investmentType)} ·{' '}
            {currencyLabel(account.currency)}{' '}
            · {account.transactionCount.toLocaleString()}{' '}
            {account.transactionCount === 1 ? 'transaction' : 'transactions'}
          </p>
          {!account.plaid && (
            <p className="text-muted-foreground m-0 mt-1 text-xs">
              Manual investment
            </p>
          )}
        </div>
        <div className="text-right">
          <p className="text-muted-foreground m-0 text-xs uppercase tracking-wide">
            Current value
          </p>
          <AmountDisplay amount={currentValue} variant="plain" align="right" />
        </div>
      </div>

      {/* Performance summary */}
      <div className="border-border bg-card rounded-xl border p-4">
        <h3 className="text-foreground m-0 mb-3 font-medium">Performance</h3>
        {performance == null ? (
          <p className="text-muted-foreground m-0 text-sm">
            {loading ? 'Loading performance…' : 'No performance data yet'}
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div>
              <div className="text-muted-foreground text-sm">Current Value</div>
              <div className="text-foreground font-medium">
                {formatCurrency(performance.totalValue)}
              </div>
            </div>
            {performance.costBasis != null && (
              <div>
                <div className="text-muted-foreground text-sm">Cost basis</div>
                <div className="text-foreground font-medium">
                  {formatCurrency(performance.costBasis)}
                </div>
              </div>
            )}
            {performance.totalGain != null && (
              <div>
                <div className="text-muted-foreground text-sm">
                  Total Gain/Loss
                </div>
                <div
                  className={`font-medium ${
                    performance.totalGain > 0
                      ? 'text-positive'
                      : performance.totalGain < 0
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-foreground'
                  }`}
                >
                  {performance.totalGain > 0 ? '+' : ''}
                  {formatCurrency(performance.totalGain)}
                </div>
              </div>
            )}
            {performance.xirr != null && (
              <div>
                <div className="text-muted-foreground text-sm">
                  Annualized Return
                </div>
                <div className="text-foreground font-medium">
                  {(performance.xirr * 100).toFixed(2)}%
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Portfolio history chart (renders its own card) */}
      <AccountPortfolioChart accountId={account._id} />

      {/* Plaid card: connection status, synced readings, and raw sync JSON.
          Renders only for Plaid-linked accounts; its data loads lazily. */}
      {account.plaid && (
        <PlaidCard
          account={account}
          onReadingChanged={() => {
            // Promoting/deleting a reading changes the derived performance,
            // the portfolio history, and Account.balance server-side.
            void refetch();
            void client.refetchQueries({
              include: ['ConsolidatedPortfolioHistory'],
            });
            refetchAccount();
          }}
        />
      )}
    </div>
  );
};
